import { memo, useMemo, useState, type ReactNode } from 'react'
import Link from 'src/components/Link'
import dynamic from 'next/dynamic'
import parse from 'html-react-parser'
import magazineUrl from 'src/functions/magazineUrl'
import { hrefForBrandPath } from 'src/functions/navigationBreadcrumbs'

/* Material */
import Accordion from '@mui/material/Accordion'
import AccordionDetails from '@mui/material/AccordionDetails'
import AccordionSummary from '@mui/material/AccordionSummary'
import Typography from '@mui/material/Typography'
import useStyles from './styles'

/* Material-Icons */
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'

/* Components */
import Modelle from './Modelle'
//import ProductPurchased from './ProductPurchased'
import AiGeneratedBadge from 'src/components/AiGeneratedBadge'
import LegalGuaranteeNotice from 'src/components/LegalGuaranteeNotice'
import { ManufacturerGuaranteeDetails } from 'src/components/ManufacturerGuarantees'
import { ManufacturerGuaranteeSnapshot } from 'src/shared/manufacturerGuarantees'
import BrandLogo from '../BrandLogo'
import { RecommendationCardsFrame, RecommendationSkeleton } from '../RecommendationShell'
import { isRenderablePurchasedProduct } from '../../functions/purchasedVisibility'
/* Types */
import { tProduct, tProductMagazineReference } from 'src/shared/frontendTypes/product'
import {
  createLandingPageImageAiGeneratedMatcher,
  type LandingPageImageAiGenerated,
} from 'src/functions/landingPageImageAiGenerated'

const ProductPurchased = dynamic(() => import('src/components/ProductPurchased/ProductPurchased'), {
  loading: ({ error }) => error
    ? <p role='status'>Empfehlungen konnten nicht geladen werden. Bitte lade die Seite erneut.</p>
    : <RecommendationSkeleton />,
})

interface AccordionItemProps {
  name: string
  title: React.ReactNode
  body: React.ReactNode
  expanded: boolean
  handleChange: (panel: string) => void
}

const AccordionItem: React.FC<AccordionItemProps> = ({
  name,
  title,
  body,
  expanded,
  handleChange,
}) => {
  const classes = useStyles()

  return (
    <Accordion
      expanded={expanded}
      onChange={() => handleChange(`panel-${name}`)}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        aria-controls={`panel-${name}-content`}
        id={`panel-${name}-header`}
      >
        <Typography className={classes.heading}>
          {title}
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        {body}
      </AccordionDetails>
    </Accordion>
  )
}

// The lightweight SSR slot and the eventual cards share one accordion and
// geometry; data and dynamic-chunk arrival do not insert another section.
export const PurchasedAccordion: React.FC<{
  product: tProduct
  alsoPurchased?: tProduct['alsoPurchased']
  expectedCount?: number
  loading?: boolean
  status?: ReactNode
}> = ({ product, alsoPurchased, expectedCount = 0, loading = false, status }) => {
  const [expanded, setExpanded] = useState(true)
  const validItems = alsoPurchased?.filter(item => 'productDetails' in item && isRenderablePurchasedProduct(item.productDetails)) || []
  if (!validItems.length && expectedCount <= 0) return null
  return <AccordionItem
    name="purchased"
    title="Kunden kauften auch:"
    body={<RecommendationCardsFrame loading={loading} status={status || (!loading && !validItems.length
      ? 'Keine weiteren Empfehlungen verfügbar.' : undefined)} count={expectedCount || validItems.length}>
      {validItems.length > 0 ? <ProductPurchased product={{ ...product, alsoPurchased: validItems }} viewType="compact" /> : null}
    </RecommendationCardsFrame>}
    expanded={expanded}
    handleChange={() => setExpanded(value => !value)}
  />
}

const normalizeMagazineImageUrl = (value?: string) => {
  const trimmed = String(value || '').trim()
  if (!trimmed) return ''
  if (trimmed.startsWith('/api/media/landingpages/')) return trimmed.replace('/api/media/landingpages/', '/api/images/landingpages/')
  if (trimmed.startsWith('/media/landingpages/')) return trimmed.replace('/media/landingpages/', '/api/images/landingpages/')
  if (trimmed.startsWith('/images/landingpages/')) return trimmed.replace('/images/landingpages/', '/api/images/landingpages/')
  return trimmed
}

const openMagazineReference = (magazine: tProductMagazineReference) => {
  if (typeof window === 'undefined') return

  const targetHash = `#magazin:${magazine.id}:${magazine.pageIndex || 0}`
  window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}`)
  window.setTimeout(() => {
    window.location.hash = targetHash
  }, 0)
}

const ProductMagazines: React.FC<{
  magazines: tProductMagazineReference[]
  landingPageImageAiGenerated?: LandingPageImageAiGenerated
}> = ({ magazines, landingPageImageAiGenerated }) => {
  const classes = useStyles()
  const isAiGeneratedLandingImage = useMemo(
    () => createLandingPageImageAiGeneratedMatcher(landingPageImageAiGenerated),
    [landingPageImageAiGenerated]
  )

  return <div className={classes.magazineGrid}>
    {magazines.map(magazine => {
      const coverUrl = normalizeMagazineImageUrl(magazine.coverImage?.src)
      const href = magazine.href || magazineUrl(magazine.id, magazine.slug, magazine.pageIndex)

      return <a
        key={magazine.id}
        href={href}
        className={classes.magazineCard}
        onClick={event => {
          if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return
          event.preventDefault()
          openMagazineReference(magazine)
        }}
      >
        <span className={classes.magazineCover}>
          {coverUrl
            ? <img src={coverUrl} alt={magazine.coverImage?.alt || magazine.title} />
            : <span>{magazine.title}</span>
          }
          <AiGeneratedBadge
            aiGenerated={magazine.coverImage?.aiGenerated || isAiGeneratedLandingImage(magazine.coverImage?.src)}
            size='compact'
            placement='top-left'
          />
        </span>
        <span className={classes.magazineMeta}>
          {magazine.issue && <small>{magazine.issue}</small>}
          <strong>{magazine.title}</strong>
          {magazine.subtitle && <span>{magazine.subtitle}</span>}
          <em>{magazine.pagesCount} Seiten</em>
        </span>
      </a>
    })}
  </div>
}

const getMagazineFaqItems = (magazines: tProductMagazineReference[]) => {
  const seen = new Set<string>()

  return magazines
    .flatMap(magazine => (magazine.faqItems || []).map(item => ({
      ...item,
      magazine,
    })))
    .filter(item => {
      const question = item.question.trim()
      const answer = item.answer.trim()
      const key = `${question.toLocaleLowerCase()}::${answer.toLocaleLowerCase()}`
      if (!question || !answer || seen.has(key)) return false
      seen.add(key)
      return true
    })
}

const renderFaqAnswer = (answer: string) =>
  answer
    .split(/\r?\n/)
    .map(part => part.trim())
    .filter(Boolean)
    .map((part, index) => <p key={index}>{part}</p>)

const ProductMagazineFaqs: React.FC<{
  magazines: tProductMagazineReference[]
  landingPageImageAiGenerated?: LandingPageImageAiGenerated
}> = ({ magazines, landingPageImageAiGenerated }) => {
  const classes = useStyles()
  const items = getMagazineFaqItems(magazines)
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: items.map(item => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer,
      },
    })),
  }

  if (items.length === 0) return <ProductMagazines
    magazines={magazines}
    landingPageImageAiGenerated={landingPageImageAiGenerated}
  />

  return <div className={classes.magazineFaq}>
    <script
      type='application/ld+json'
      dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
    />
    <p className={classes.magazineFaqIntro}>
      Passende Hinweise zu Pflege, Material, Nutzung und Auswahl. Die Inhalte werden sichtbar ausgespielt und als strukturierte FAQ-Daten markiert.
    </p>
    {items.map(item => {
      const href = item.magazine.href || magazineUrl(item.magazine.id, item.magazine.slug, item.magazine.pageIndex)

      return <article key={`${item.magazine.id}-${item.question}`} className={classes.magazineFaqItem}>
        {item.topic && <small>{item.topic}</small>}
        <h3>{item.question}</h3>
        <div>{renderFaqAnswer(item.answer)}</div>
        <a
          href={href}
          onClick={event => {
            if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return
            event.preventDefault()
            openMagazineReference(item.magazine)
          }}
        >
          Weiter im Ratgeber
        </a>
      </article>
    })}
  </div>
}

const BrandDetails = memo(({ product }: { product: tProduct }) => {
  const classes = useStyles()
  const brandHref = hrefForBrandPath(product.brandPath, product.brand)
  const description = useMemo(() => product.brandDescription
    ? parse(product.brandDescription, { trim: true })
    : null, [product.brandDescription])
  const manufacturerAddress = useMemo(() => parse(product.manufacturerAddress || '', { trim: true }), [product.manufacturerAddress])

  return <div className={classes.brandDetails}>
    {(product.brandLogo || brandHref) &&
      <div className={classes.brandDetailsHeader}>
        {product.brandLogo &&
          <div className={classes.brandDetailsLogo}>
            <BrandLogo logo={product.brandLogo} title={product.brandTitel} large />
          </div>
        }

        <div className={classes.brandDetailsText}>
          <strong>{product.brandTitel}</strong>
          {brandHref &&
            <Link href={brandHref} passHref legacyBehavior>
              <a>Alle Artikel von {product.brandTitel} ansehen</a>
            </Link>
          }
        </div>
      </div>
    }

    {description && <div className={classes.brandDescription}>{description}</div>}

    {product.manufacturerAddress &&
      <div className={classes.manufacturerAddress}>
        <b>Herstellerangaben im Sinne der Produktsicherheitsverordnung (GPSR):</b>
        <br />
        {manufacturerAddress}
      </div>
    }
  </div>
})
BrandDetails.displayName = 'BrandDetails'

const DescriptionAccordion: React.FC<{
  product: tProduct
  alsoPurchased?: tProduct['alsoPurchased']
  landingPageImageAiGenerated?: LandingPageImageAiGenerated
  manufacturerGuarantees?: ManufacturerGuaranteeSnapshot[]
}> = ({ product, alsoPurchased = product.alsoPurchased, landingPageImageAiGenerated, manufacturerGuarantees = [] }) => {
  const classes = useStyles()
  const validPurchased = alsoPurchased?.filter(item => 'productDetails' in item && isRenderablePurchasedProduct(item.productDetails)) || []
  // Mehrere Panels sollen standardmäßig ausgeklappt sein (hier: "description" und "purchased")
  const [expanded, setExpanded] = useState<string[]>([
    'panel-description',
    'panel-magazineFaq',
    'panel-purchased',
  ])

  const handleChange = (panel: string) => {
    setExpanded((prev) =>
      prev.includes(panel)
        ? prev.filter((p) => p !== panel)
        : [...prev, panel]
    )
  }
  const description = useMemo(() => parse(product.description || '', { trim: true }), [product.description])
  const descriptionDetails = useMemo(() => parse(product.descriptionDetails || '', { trim: true }), [product.descriptionDetails])
  const magazineReferences = product.magazines || []
  const faqMagazines = magazineReferences.filter(magazine =>
    (magazine.mode || 'magazine') === 'faq' && (magazine.faqItems || []).length > 0
  )
  const editorialMagazines = magazineReferences.filter(magazine => (magazine.mode || 'magazine') !== 'faq')

  return (
    <div className={classes.root}>
      <AccordionItem
        name="description"
        title="Produktdetails"
        body={description}
        expanded={expanded.includes('panel-description')}
        handleChange={handleChange}
      />

      <AccordionItem
        name="descriptionDetails"
        title="Produktbeschreibung"
        body={descriptionDetails}
        expanded={expanded.includes('panel-descriptionDetails')}
        handleChange={handleChange}
      />

      {!!product?.combinations.length && (
        <AccordionItem
          name="modelle"
          title="Produktausführungen"
          body={expanded.includes('panel-modelle') ? <Modelle product={product} /> : null}
          expanded={expanded.includes('panel-modelle')}
          handleChange={handleChange}
        />
      )}

      {faqMagazines.length > 0 && (
        <AccordionItem
          name="magazineFaq"
          title="Pflege, Beratung & häufige Fragen"
          body={<ProductMagazineFaqs
            magazines={faqMagazines}
            landingPageImageAiGenerated={landingPageImageAiGenerated}
          />}
          expanded={expanded.includes('panel-magazineFaq')}
          handleChange={handleChange}
        />
      )}

      {editorialMagazines.length > 0 && (
        <AccordionItem
          name="magazines"
          title="In Magazinen entdecken"
          body={<ProductMagazines
            magazines={editorialMagazines}
            landingPageImageAiGenerated={landingPageImageAiGenerated}
          />}
          expanded={expanded.includes('panel-magazines')}
          handleChange={handleChange}
        />
      )}

      <AccordionItem
        name="brand"
        title={
          <>
            Über <strong>{product.brandTitel}</strong> & Hersteller-Informationen
          </>
        }
        body={<BrandDetails product={product} />}
        expanded={expanded.includes('panel-brand')}
        handleChange={handleChange}
      />

      <AccordionItem
        name="legalGuarantee"
        title="Gesetzliche Gewährleistung"
        body={<LegalGuaranteeNotice />}
        expanded={expanded.includes('panel-legalGuarantee')}
        handleChange={handleChange}
      />

      {manufacturerGuarantees.length > 0 && <AccordionItem
        name="manufacturerGuarantee"
        title="Herstellergarantie und Garantiebedingungen"
        body={<ManufacturerGuaranteeDetails guarantees={manufacturerGuarantees} />}
        expanded={expanded.includes('panel-manufacturerGuarantee')}
        handleChange={handleChange}
      />}

      {validPurchased.length > 0 && (
        <AccordionItem
          name="purchased"
          title="Kunden kauften auch:"
          body={<RecommendationCardsFrame><ProductPurchased product={{ ...product, alsoPurchased: validPurchased }} viewType="compact" /></RecommendationCardsFrame>}
          expanded={expanded.includes('panel-purchased')}
          handleChange={handleChange}
        />
      )}
    </div>
  )
}

export default memo(DescriptionAccordion)
